/**
 * AdminSidebar -- role-aware sidebar navigation for admin pages.
 *
 * 13 navigation items in 3 groups (Observar/Gerenciar/Configurar)
 * plus an ungrouped "Visao Geral" at the top.
 *
 * Groups are collapsible, default ALL EXPANDED on first visit.
 * Collapse/expand state persisted to localStorage.
 * Sidebar collapse (icon-only mode) persisted separately per area.
 *
 * Uses <AppSidebar> as the shared layout primitive and <AreaSwitcher>
 * as the pinned-bottom area switcher (Chat / Code / Admin).
 *
 * Resize range: 220-380px, default 260px.
 * Import SidebarResizeHandle from '@/components/layout/SidebarResizeHandle'.
 */

import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  Users,
  GearSix,
  ChartBar,
  ClipboardText,
  CaretLeft,
  CaretRight,
  CaretDown,
  SquaresFour,
  Faders,
  Gauge,
  Cube,
  Scales,
  ArrowsLeftRight,
  ShieldWarning,
  HardDrives,
  // --- FEAT-035 TASK-006 connections-group ---
  PlugsConnected,
  Key,
  Cloud,
  ShareNetwork,
  // --- fim FEAT-035 TASK-006 connections-group ---
  // --- FEAT-038 TASK-010 (nav) ---
  Wallet,
  // --- fim FEAT-038 TASK-010 (nav) ---
  // --- FEAT-039 TASK-010 (nav-icon) ---
  ChartLineUp,
  // --- fim FEAT-039 TASK-010 (nav-icon) ---
  // --- FEAT-040 TASK-011 (icons) ---
  ListMagnifyingGlass,
  Pulse,
  // --- fim FEAT-040 TASK-011 (icons) ---
} from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import { useAuthStore } from '@/stores/useAuthStore';
import { hasMinimumRole } from '@/components/guards/RoleGuard';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { AreaSwitcher } from '@/components/layout/AreaSwitcher';
import { SidebarHeaderActions } from '@/components/layout/SidebarHeaderActions';
import { SidebarResizeHandle } from '@/components/layout/SidebarResizeHandle';
import { Tooltip } from '@/components/ui/Tooltip';
import { BrandLogo } from '@/components/BrandLogo';
import { useBranding } from '@/hooks/useBranding';
import { useSidebarResize } from '@/hooks/useSidebarResize';
import { cn } from '@/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type OrgRole = 'admin' | 'maintainer' | 'member';

interface NavItem {
  id: string;
  labelId: string;
  path: string;
  icon: ComponentType<IconProps>;
  minimumRole: OrgRole;
  /** When true, active state requires exact pathname match (no prefix). */
  exactMatch?: boolean;
}

interface NavGroup {
  id: string;
  labelId: string;
  items: NavItem[];
}

// ---------------------------------------------------------------------------
// Navigation definition -- ia-map.md section 2
// ---------------------------------------------------------------------------

/** Top-level overview item (no group). */
const OVERVIEW_ITEM: NavItem = {
  id: 'overview',
  labelId: 'admin.nav.overview',
  path: '/admin',
  icon: SquaresFour,
  minimumRole: 'maintainer',
  exactMatch: true,
};

/** Grouped navigation items. */
const NAV_GROUPS: NavGroup[] = [
  // --- FEAT-035 TASK-006 connections-group ---
  // Owner: FEAT-035 (D16). First group, admin-only -- empty (hidden) for maintainers.
  // Created by FEAT-033 with the api-keys item; FEAT-035 wraps it, renames the id and adds the rest.
  // Later wave-3 features (FEAT-036, FEAT-038) write INSIDE this anchor only.
  {
    id: 'connections',
    labelId: 'admin.nav.group.connections',
    items: [
      {
        id: 'connections-endpoints',
        labelId: 'admin.nav.connections.endpoints',
        path: '/admin/connections/endpoints',
        icon: PlugsConnected,
        minimumRole: 'admin',
      },
      {
        id: 'connections-api-keys',
        labelId: 'admin.nav.connections.apiKeys',
        path: '/admin/connections/api-keys',
        icon: Key,
        minimumRole: 'admin',
      },
      {
        // Owner decision L2 (design.md): Cloud, not Plugs (Plugs is the sidebar header CTA).
        id: 'connections-providers',
        labelId: 'admin.nav.connections.providers',
        path: '/admin/connections/providers',
        icon: Cloud,
        minimumRole: 'admin',
      },
      {
        id: 'connections-quota-pools',
        labelId: 'admin.nav.connections.quotaPools',
        path: '/admin/connections/quota-pools',
        icon: ShareNetwork,
        minimumRole: 'admin',
      },
    ],
  },
  // --- fim FEAT-035 TASK-006 connections-group ---
  {
    id: 'observe',
    labelId: 'admin.nav.group.observe',
    items: [
      {
        id: 'reports',
        labelId: 'admin.nav.reports',
        path: '/admin/reports',
        icon: ChartBar,
        minimumRole: 'maintainer',
      },
      // --- FEAT-038 TASK-010 (nav) ---
      {
        id: 'costs',
        labelId: 'admin.nav.costs',
        path: '/admin/costs',
        icon: Wallet,
        minimumRole: 'maintainer',
      },
      // --- fim FEAT-038 TASK-010 (nav) ---
      // --- FEAT-039 TASK-010 (nav) ---
      {
        id: 'analytics',
        labelId: 'admin.nav.analytics',
        path: '/admin/analytics',
        icon: ChartLineUp,
        minimumRole: 'maintainer',
      },
      // --- fim FEAT-039 TASK-010 (nav) ---
      {
        id: 'compliance',
        labelId: 'admin.nav.compliance',
        path: '/admin/compliance',
        icon: ShieldWarning,
        minimumRole: 'maintainer',
      },
      {
        id: 'usage-compliance',
        labelId: 'admin.nav.usageCompliance',
        path: '/admin/usage-compliance',
        icon: Scales,
        minimumRole: 'maintainer',
      },
      {
        id: 'comparisons',
        labelId: 'admin.nav.comparisons',
        path: '/admin/comparisons',
        icon: ArrowsLeftRight,
        minimumRole: 'maintainer',
      },
      // --- FEAT-040 TASK-011 (nav) ---
      {
        id: 'logs',
        labelId: 'admin.nav.logs',
        path: '/admin/logs',
        icon: ListMagnifyingGlass,
        minimumRole: 'admin',
      },
      {
        id: 'operations',
        labelId: 'admin.nav.operations',
        path: '/admin/operations',
        icon: Pulse,
        minimumRole: 'admin',
      },
      // --- fim FEAT-040 TASK-011 (nav) ---
    ],
  },
  {
    id: 'manage',
    labelId: 'admin.nav.group.manage',
    items: [
      {
        id: 'people',
        labelId: 'admin.nav.people',
        path: '/admin/people',
        icon: Users,
        minimumRole: 'maintainer',
      },
      {
        id: 'models',
        labelId: 'admin.nav.models',
        path: '/admin/models',
        icon: Cube,
        minimumRole: 'maintainer',
      },
      {
        id: 'quotas',
        labelId: 'admin.nav.quotas',
        path: '/admin/quotas',
        icon: Gauge,
        minimumRole: 'maintainer',
      },
    ],
  },
  {
    id: 'configure',
    labelId: 'admin.nav.group.configure',
    items: [
      {
        id: 'settings',
        labelId: 'admin.nav.settings',
        path: '/admin/settings',
        icon: GearSix,
        minimumRole: 'admin',
      },
      {
        id: 'classifier',
        labelId: 'admin.nav.classifier',
        path: '/admin/classifier',
        icon: Faders,
        minimumRole: 'admin',
      },
      {
        id: 'audit-log',
        labelId: 'admin.nav.auditLog',
        path: '/admin/audit-log',
        icon: ClipboardText,
        minimumRole: 'admin',
      },
      {
        id: 'fleet',
        labelId: 'admin.nav.fleet',
        path: '/admin/fleet',
        icon: HardDrives,
        minimumRole: 'admin',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLLAPSED_STORAGE_KEY = 'opus-ia-admin-sidebar-collapsed';
const GROUPS_STORAGE_KEY = 'opus-ia-admin-sidebar-groups-expanded';
const SIDEBAR_WIDTH_KEY = 'opus-ia-sidebar-width';
const SIDEBAR_DEFAULT_WIDTH = 260;
const SIDEBAR_MIN_WIDTH = 220;
const SIDEBAR_MAX_WIDTH = 380;

function readExpandedGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
    if (!raw) {
      // Default: all groups expanded
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, boolean>;
    }
    return {};
  } catch {
    return {};
  }
}

/** Default all groups to expanded when no stored state exists. */
function isGroupExpanded(
  stored: Record<string, boolean>,
  groupId: string,
  hasStoredState: boolean,
): boolean {
  if (!hasStoredState) return true; // First visit: all expanded
  return stored[groupId] !== false; // Expanded unless explicitly closed
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminSidebar() {
  const user = useAuthStore((s) => s.user);
  const intl = useIntl();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Persist collapse state
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
    } catch {
      // localStorage not available
    }
  }, [collapsed]);

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  // Per-group expand state. Default: ALL EXPANDED on first visit.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    () => readExpandedGroups(),
  );
  const [hasStoredGroupState] = useState(
    () => localStorage.getItem(GROUPS_STORAGE_KEY) !== null,
  );

  useEffect(() => {
    try {
      localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(expandedGroups));
    } catch {
      // localStorage not available
    }
  }, [expandedGroups]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: prev[groupId] === false ? true : false,
    }));
  }, []);

  // Sidebar resize (desktop only, disabled when collapsed)
  const { width: resizeWidth, isResizing, handleProps } = useSidebarResize({
    enabled: !collapsed,
    storageKey: SIDEBAR_WIDTH_KEY,
    minWidth: SIDEBAR_MIN_WIDTH,
    maxWidth: SIDEBAR_MAX_WIDTH,
    defaultWidth: SIDEBAR_DEFAULT_WIDTH,
  });

  if (!user) return null;

  const userRole = user.role;
  const { platform_name } = useBranding();

  // Filter groups to only include items the user can see.
  // Empty groups (after filtering) are not rendered.
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) =>
      hasMinimumRole(userRole, item.minimumRole),
    ),
  })).filter((group) => group.items.length > 0);

  // -- Header slot: logo + collapse toggle ----------------------------------
  const headerSlot = (
    <div
      className={cn(
        'flex h-[var(--topbar-height)] items-center justify-between px-3',
      )}
    >
      {!collapsed && (
        <div className="flex items-center gap-2">
          <NavLink
            to="/"
            className="inline-flex items-center transition-opacity hover:opacity-80"
            aria-label={platform_name}
          >
            <BrandLogo variant="sidebar" />
          </NavLink>
          <SidebarHeaderActions />
        </div>
      )}
      <button
        type="button"
        onClick={toggleCollapse}
        className={cn(
          'inline-flex items-center justify-center rounded-md p-1.5 text-text-tertiary transition-colors',
          'hover:bg-bg-tertiary hover:text-text-primary',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
          collapsed && 'mx-auto',
        )}
        aria-label={intl.formatMessage({
          id: collapsed
            ? 'admin.sidebar.expand'
            : 'admin.sidebar.collapse',
        })}
      >
        {collapsed ? <CaretRight size={16} /> : <CaretLeft size={16} />}
      </button>
    </div>
  );

  // -- Scrollable slot: Overview link + nav groups --------------------------
  const scrollableSlot = (
    <nav
      className="px-2 py-3"
      aria-label={intl.formatMessage({ id: 'admin.sidebar.navLabel' })}
    >
      {/* Overview -- top-level admin home link (no group) */}
      <ul role="list" className="mb-4 space-y-0.5">
        <li>
          <NavItemLink
            item={OVERVIEW_ITEM}
            collapsed={collapsed}
            currentPath={location.pathname}
            intl={intl}
          />
        </li>
      </ul>

      {/* Navigation groups -- collapsible (default all expanded).
          When sidebar is icon-collapsed, group expand state is bypassed
          and all items render as a flat icon list. */}
      {visibleGroups.map((group) => {
        const isExpanded = collapsed
          ? true
          : isGroupExpanded(expandedGroups, group.id, hasStoredGroupState);
        const headerId = `admin-nav-group-${group.id}`;
        const listId = `admin-nav-group-${group.id}-list`;
        return (
          <div key={group.id} className="mb-2">
            {!collapsed && (
              <button
                type="button"
                id={headerId}
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isExpanded}
                aria-controls={listId}
                className={cn(
                  'flex items-center justify-between w-full px-3 py-1.5 mx-1.5 mt-3',
                  'text-xs font-semibold uppercase tracking-wider text-text-tertiary',
                  'hover:text-text-secondary transition-colors',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
                  'rounded-md',
                )}
              >
                <span>
                  <FormattedMessage id={group.labelId} />
                </span>
                <CaretDown
                  size={12}
                  className={cn(
                    'shrink-0 transition-transform duration-150',
                    !isExpanded && '-rotate-90',
                  )}
                  aria-hidden="true"
                />
              </button>
            )}
            {isExpanded && (
              <ul
                role="list"
                id={listId}
                aria-labelledby={collapsed ? undefined : headerId}
                className={cn('space-y-0.5', !collapsed && 'mt-1')}
              >
                {group.items.map((item) => (
                  <li key={item.id}>
                    <NavItemLink
                      item={item}
                      collapsed={collapsed}
                      currentPath={location.pathname}
                      intl={intl}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );

  // User identity card removed -- now lives in AppTopBar's UserMenu

  return (
    <div className="relative h-full shrink-0">
      <AppSidebar
        collapsed={collapsed}
        ariaLabel={intl.formatMessage({ id: 'admin.sidebar.label' })}
        className={cn(
          'transition-[width] duration-200',
          collapsed && 'w-16',
          // When resizing, disable transition for smooth drag
          isResizing && '!transition-none',
        )}
        style={collapsed ? undefined : { width: `${resizeWidth}px` }}
        header={headerSlot}
        scrollable={scrollableSlot}
        areaSwitcher={<AreaSwitcher collapsed={collapsed} />}
      />
      {/* Resize handle -- only when expanded */}
      {!collapsed && (
        <SidebarResizeHandle
          onMouseDown={handleProps.onMouseDown}
          onDoubleClick={handleProps.onDoubleClick}
          isResizing={isResizing}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NavItemLink -- individual navigation link (design.md C.3)
// ---------------------------------------------------------------------------

interface NavItemLinkProps {
  item: NavItem;
  collapsed: boolean;
  currentPath: string;
  intl: ReturnType<typeof useIntl>;
}

function NavItemLink({ item, collapsed, currentPath, intl }: NavItemLinkProps) {
  const Icon = item.icon;

  // Active state: exactMatch for /admin overview, prefix for others.
  // Prefix matching excludes paths that share a common prefix but diverge
  // (e.g., /admin/compliance vs /admin/comparisons).
  const isActive = item.exactMatch
    ? currentPath === item.path
    : currentPath === item.path || currentPath.startsWith(item.path + '/');

  const label = intl.formatMessage({ id: item.labelId });

  const linkContent = (
    <span
      className={cn(
        // Base item recipe (design.md C.3)
        'flex items-center gap-3 px-3 py-2.5 mx-1.5 mb-0.5 rounded-lg',
        'text-sm transition-colors duration-150',
        'border border-transparent',
        // Active state (design.md C.3)
        isActive
          ? 'bg-sidebar-item-active-bg text-sidebar-item-active-text font-medium border border-accent-500/20 shadow-sm'
          : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary',
        collapsed && 'justify-center mx-0 px-0',
      )}
    >
      <Icon
        size={20}
        weight={isActive ? 'fill' : 'regular'}
        className="shrink-0"
        aria-hidden="true"
      />
      {!collapsed && <span className="truncate">{label}</span>}
    </span>
  );

  // Collapsed: wrap in tooltip showing the label
  if (collapsed) {
    return (
      <Tooltip content={label} position="right">
        <NavLink
          to={item.path}
          end={item.exactMatch}
          aria-label={label}
          aria-current={isActive ? 'page' : undefined}
        >
          {linkContent}
        </NavLink>
      </Tooltip>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.exactMatch}
      aria-label={label}
      aria-current={isActive ? 'page' : undefined}
    >
      {linkContent}
    </NavLink>
  );
}
